import React from 'react';
import { RangeIndicator } from '@benzinga/core-ui';
import styled from '@benzinga/themetron';
import { Icon, Tooltip } from '@benzinga/core-ui';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';

export interface RatingsProps {
  up: number;
  down: number;
}

export interface TechnicalsFinancialsAnalysisProps {
  technicals: RatingsProps;
  financials: RatingsProps;
}

export const TechnicalsFinancialsAnalysis: React.FC<TechnicalsFinancialsAnalysisProps> = ({
  financials,
  technicals,
}) => {
  const technicalAnalysisRating = Math.floor((technicals.up / (technicals.down + technicals.up)) * 100);
  const financialAnalysisRating = Math.floor((financials.up / (financials.down + financials.up)) * 100);

  if (!technicalAnalysisRating || !financialAnalysisRating) {
    return null;
  }

  const isEnd = value => {
    if (value < 5) {
      return 'hide-start';
    }
    if (value > 95) {
      return 'hide-end';
    }
    return '';
  };

  return (
    <Container className="technicals-financials-analysis">
      <div className="ratings-section">
        {!!technicalAnalysisRating && (
          <div className={`range-indicator-wrapper ${isEnd(technicalAnalysisRating)}`}>
            <InfoTooltip
              title="Technicals Analysis"
              tooltipContent="Scores stock's market momentum out of 100, using buy/sell ratings, RSI, and price trends."
            />
            <RangeIndicator value={technicalAnalysisRating} />
          </div>
        )}
        {!!technicalAnalysisRating && !!financialAnalysisRating && <Divider />}
        {!!financialAnalysisRating && (
          <div className={`range-indicator-wrapper ${isEnd(financialAnalysisRating)}`}>
            <InfoTooltip
              title="Financials Analysis"
              tooltipContent="Rates stock's financial health out of 100, based on EPS, PE, PB ratios, ROE, and debt equity ratio."
            />
            <RangeIndicator value={financialAnalysisRating} />
          </div>
        )}
      </div>
    </Container>
  );
};

const InfoTooltip = ({ title, tooltipContent }) => (
  <div className="flex text-gray-600">
    <div className="info-tooltip-title text-gray-600 font-bold uppercase mr-2">{title}</div>
    <Tooltip content={tooltipContent} width={250}>
      <Icon className="info-circle-icon text-sm flex items-center justify-center" icon={faInfoCircle} />
    </Tooltip>
  </div>
);

const Divider = () => {
  return (
    <DividerWrapper className="divider-wrapper">
      <div className="divider" />
    </DividerWrapper>
  );
};

const DividerWrapper = styled.div`
  padding: 2px 16px;

  .divider {
    background: #e5e5e5;
    width: 2px;
    height: 100%;

    @media (max-width: 768px) {
      width: 100%;
      height: 2px;
    }
  }
`;

const Container = styled.div`
  .overview-title {
    color: #5b7292;
    text-transform: uppercase;
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;
  }

  position: relative;
  border-radius: 4px;
  padding: 12px 16px;
  /* border: 1px solid #ceddf2;
  background: #f2f8ff; */
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    height: fit-content;
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
    align-items: center;

    .ratings-section {
      width: 100%;
    }

    .range-indicator-wrapper {
      width: 100%;
      display: flex;
      flex-direction: column;
    }

    &.overview-section {
      padding: 0 8px;
    }
  }

  .ratings-circle {
    padding: 4px;
    background: #f2f8ff;
    margin-right: 1rem;
    @media (min-width: 769px) {
      border: 1px solid #ceddf2;
      border-right: none;
      border-top-left-radius: 50%;
      border-bottom-left-radius: 50%;
      position: absolute;
      left: -32px;
      top: 50%;
      transform: translateY(-50%);
    }
  }

  .ratings-section {
    display: flex;

    @media (max-width: 768px) {
      flex-direction: column;
      gap: 4px;
    }
  }

  .range-indicator-values-container {
    margin-top: 2px;
  }

  .hide-end .range-indicator-max-value {
    display: none;
  }

  .hide-start .range-indicator-min-value {
    display: none;
  }
`;
